/**
 * AutosaveIndicator Component
 */

import React, { useMemo } from 'react';
import type { AutosaveStatus } from '@hooks/useAutosave';
import styles from './AutoSaveIndicator.module.css';

// =============================================================================
// TYPES
// =============================================================================

interface AutosaveIndicatorProps {
  status: AutosaveStatus;
  lastSavedAt: number | null;
  error: string | null;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const AutosaveIndicator: React.FC<AutosaveIndicatorProps> = ({
  status,
  lastSavedAt,
  error,
}) => {
  // Format timestamp
  const formattedTime = useMemo(() => {
    if (!lastSavedAt) return null;
    
    const date = new Date(lastSavedAt);
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }, [lastSavedAt]);

  // Don't render anything when idle and never saved
  if (status === 'idle' && !lastSavedAt) {
    return null;
  }

  return (
    <div 
      className={`${styles.indicator} ${styles[status]}`}
      role="status"
      aria-live="polite"
    >
      {status === 'saving' && (
        <>
          <span className={styles.spinner} aria-hidden="true" />
          <span className={styles.text}>Saving...</span>
        </>
      )}

      {status === 'saved' && formattedTime && (
        <>
          <span className={styles.checkmark} aria-hidden="true">✓</span>
          <span className={styles.text}>Saved at {formattedTime}</span>
        </>
      )}

      {status === 'pending' && formattedTime && (
        <span className={styles.text}>Last saved {formattedTime}</span>
      )}

      {status === 'error' && (
        <>
          <span className={styles.errorIcon} aria-hidden="true">!</span>
          <span className={styles.text} title={error || 'Save failed'}>
            Save failed
          </span>
        </>
      )}

      {status === 'idle' && formattedTime && (
        <span className={styles.text}>Saved at {formattedTime}</span>
      )}
    </div>
  );
};

export default AutosaveIndicator;